import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import TextBlock from "./TextBlock";
import { categoryColor, displayTerm } from "../graphlab/graphShared";
import { glossTitle } from "../../utils/termGlosses";
import { siteUrl } from "../../utils/constants";

/** Case-chapter block (element.evidence-findings): the quantified
 *  findings, live from the evidence analytics API — the same pattern as
 *  the treemap chapter, so every figure follows index rebuilds instead
 *  of freezing in prose. Panels: the then-vs-now shift behind finding 1
 *  (first three crawl years against the last three), collection keyness
 *  (Dunning G²), source concentration (effective domains) and the
 *  discourse communities. */

// Geography terms are proper nouns stored lowercase; slot 4 is fixed by
// the same id convention the palette uses (see graphShared).
const GEOGRAPHY_CATEGORY = 4;
const termLabel = (term: string, category: number) => {
  // Sentence-case every chart label: raw lowercase lexicon entries
  // read poorly on axes.
  const display = displayTerm(term, category === GEOGRAPHY_CATEGORY);
  return display.charAt(0).toUpperCase() + display.slice(1);
};

const HRDI = "humanRightsDocumentationInitiative";
const TRC = "truthAndReconciliation";

interface TrendTerm {
  term: string;
  category: number;
  pages: number;
  series: number[];
}
interface CategoryShare {
  id: number;
  label: string;
  series: number[];
}
interface Trends {
  years: string[];
  pagesByYear: number[];
  categoryShares: CategoryShare[];
  terms: TrendTerm[];
}
interface Tie {
  a: string;
  b: string;
  pmi: number;
  co: number;
  catA: number;
  catB: number;
}
interface CommunityPlace {
  place: string;
  passages: number;
  sites: number;
  top_share: number;
  lift: number;
}
interface Community {
  name: string;
  size: number;
  composition: { category: number; label: string; terms: number }[];
  members: string[];
  // October 2026: 'looser' = the terms cluster, but only a small core
  // holds together reliably; peripheral terms join about half the time
  tier?: "stable" | "looser";
  peripheral?: string[];
  // September 2026 pipeline: places are context, attached with evidence
  places?: CommunityPlace[];
}
// A place's coverage is flagged as single-source when one archived site
// supplies at least this share of its passages.
const SINGLE_SOURCE_SHARE = 0.5;
// Finding 1's terms, fixed so the chart shows exactly what the chapter
// argues with; the values themselves are live.
const SHIFT_GROWING = ["victim", "truth", "testimony", "reparation",
  "genocide", "justice"];
const SHIFT_RECEDING = ["monument", "disappeared", "memorial"];
interface KeynessTerm {
  term: string;
  category: number;
  shareA: number;
  shareB: number;
  g2: number;
  favors: "A" | "B";
}
interface Keyness {
  setA: string;
  setB: string;
  pagesA: number;
  pagesB: number;
  terms: KeynessTerm[];
}
interface SourceTerm {
  term: string;
  category: number;
  pages: number;
  domains: number;
  effectiveDomains: number;
  topDomain: string;
  topShare: number;
}

const styles = `
.evf-panel { margin: 48px 0; }
.evf-panel h5 { font-size: 18px; margin-bottom: 6px; }
.evf-lede { font-size: 15px; color: #757575; margin-bottom: 18px;
  max-width: 720px; }
.evf-figure { border: 1px solid #dee2e6; border-radius: 4px;
  background: #fff; padding: 18px 18px 8px; }
.evf-caption { font-size: 13px; color: #757575; margin: 10px 4px 0; }
.evf-shift { padding: 12px 18px 10px; }
.evf-shift table { width: 100%; border-collapse: collapse; font-size: 14.5px;
  font-variant-numeric: tabular-nums; }
.evf-shift th, .evf-shift td { padding: 6px 8px; text-align: right; }
.evf-shift thead th { font-size: 12.5px; color: #757575; font-weight: 600;
  border-bottom: 1px solid #dee2e6; }
.evf-shift td.evf-shift-term { text-align: left; }
.evf-shift .evf-shift-group th { text-align: left; font-size: 12.5px;
  text-transform: uppercase; letter-spacing: .04em; color: #757575;
  font-weight: 600; padding-top: 14px; }
.evf-shift tbody tr + tr td { border-top: 1px solid #f1f3f5; }
.evf-shift .growing .evf-shift-change { color: #2f6db5; }
.evf-shift .receding .evf-shift-change { color: #c0612b; }
.evf-mover { display: flex; align-items: center; gap: 10px;
  font-size: 14px; margin-bottom: 7px; }
.evf-mover .term { width: 110px; flex: none; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.evf-mover .bar { height: 8px; border-radius: 4px; }
.evf-mover .delta { color: #757575; font-size: 12.5px;
  font-variant-numeric: tabular-nums; }
.evf-source { display: flex; align-items: center; gap: 12px;
  font-size: 14px; margin-bottom: 9px; }
.evf-source .term { width: 110px; flex: none; }
.evf-source .track { flex: 1; height: 10px; background: #f1efec;
  border-radius: 5px; overflow: hidden; }
.evf-source .fill { display: block; height: 100%; border-radius: 5px; }
.evf-source .who { width: 240px; flex: none; font-size: 12.5px;
  color: #757575; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; }
@media (max-width: 768px) { .evf-source .who { width: 120px; } }
.evf-community { border: 1px solid #dee2e6; border-radius: 4px;
  padding: 14px 16px; margin-bottom: 12px; }
.evf-community-head { display: flex; justify-content: space-between;
  align-items: baseline; gap: 12px; font-size: 15px; }
.evf-community-head span { font-size: 12.5px; color: #757575; }
.evf-tier { font-size: 11.5px; color: #757575; border: 1px solid #dee2e6;
  border-radius: 10px; padding: 0 8px; margin-left: 8px; font-weight: 400; }
.evf-peripheral { font-size: 13px; color: #757575; margin: 2px 0 0; }
.evf-ties { margin-top: 18px; }
.evf-ties h6 { font-size: 14px; margin-bottom: 4px; }
.evf-ties .evf-ties-lede { font-size: 12.5px; color: #757575; margin-bottom: 10px; }
.evf-tie { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.evf-tie .pair { min-width: 240px; font-size: 14px; }
.evf-tie .bar { height: 8px; border-radius: 4px; }
.evf-tie .pmi { font-size: 12.5px; color: #757575; white-space: nowrap; }
.evf-comp-bar { display: flex; height: 10px; border-radius: 5px;
  overflow: hidden; margin: 10px 0 8px; }
.evf-comp-bar span { display: block; height: 100%; }
.evf-members { font-size: 13.5px; color: #757575; margin: 0; }
`;

type Panel = "trends" | "keyness" | "discourses" | "sources";

interface EvidenceFindingsBlockProps {
  heading?: string;
  set?: string;
  compareSet?: string;
  /** Render one panel only, so charts interleave with the chapter's
   *  text blocks. Omitted → all panels (original stacked layout). */
  panel?: Panel;
}

const EvidenceFindingsBlock = ({
  heading, set = HRDI, compareSet = TRC, panel,
}: EvidenceFindingsBlockProps) => {
  const { t, i18n } = useTranslation();
  const api = `${siteUrl}/api/evidence`;
  const [trends, setTrends] = useState<Trends | null>(null);
  const [keyness, setKeyness] = useState<Keyness | null>(null);
  const [sources, setSources] = useState<SourceTerm[] | null>(null);
  const [communities, setCommunities] = useState<Community[] | null>(null);
  const [ties, setTies] = useState<Tie[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Fetch only what the rendered panel needs — a chapter interleaving
  // four single-panel instances must not issue four full fetch fans.
  const wants = (p: Panel) => !panel || panel === p;
  useEffect(() => {
    let alive = true;
    const guard = <T,>(setter: (v: T) => void) => (v: T) => {
      if (alive) setter(v);
    };
    const jobs: Promise<void>[] = [];
    if (wants("trends")) {
      jobs.push(axios.get(`${api}/trends`, { params: { set } })
        .then(({ data }) => guard(setTrends)(data)));
    }
    if (wants("keyness")) {
      jobs.push(axios.get(`${api}/keyness`,
        { params: { setA: set, setB: compareSet } })
        .then(({ data }) => guard(setKeyness)(data)));
    }
    if (wants("sources")) {
      jobs.push(axios.get(`${api}/sources`, { params: { set } })
        .then(({ data }) => guard(setSources)(data.terms)));
    }
    if (wants("discourses")) {
      jobs.push(axios.get(`${api}/discourses`, { params: { set } })
        .then(({ data }) => guard(setCommunities)(data.communities || [])));
      jobs.push(axios.get(`${api}/ties`, { params: { set } })
        .then(({ data }) => guard(setTies)(data.edges || []))
        .catch(() => guard(setTies)([])));
    }
    Promise.all(jobs).catch(() => alive && setFailed(true));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, set, compareSet, panel]);

  // ---- Panel 1: then vs now for the terms finding 1 argues with ----
  const shift = useMemo(() => {
    if (!trends || trends.years.length < 6) return null;
    const n = trends.years.length;
    const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
    const pages = (from: number, to: number) => trends.pagesByYear
      .slice(from, to).reduce((a, b) => a + b, 0);
    const row = (term: string) => {
      const found = trends.terms.find((x) => x.term === term);
      if (!found) return null;
      return {
        term, category: found.category,
        early: mean(found.series.slice(0, 3)) * 100,
        late: mean(found.series.slice(n - 3)) * 100,
      };
    };
    const pick = (terms: string[]) => terms.map(row)
      .filter((r): r is NonNullable<ReturnType<typeof row>> => r !== null);
    const groups = [
      { key: "growing", terms: pick(SHIFT_GROWING) },
      { key: "receding", terms: pick(SHIFT_RECEDING) },
    ].filter((g) => g.terms.length > 0);
    if (!groups.length) return null;
    return {
      groups,
      early: `${trends.years[0]}–${trends.years[2].slice(2)}`,
      late: `${trends.years[n - 3]}–${trends.years[n - 1].slice(2)}`,
      earlyPages: pages(0, 3), latePages: pages(n - 3, n),
    };
  }, [trends]);

  // ---- Panel 2: keyness diverging bars ----
  const keynessRows = useMemo(() => {
    if (!keyness) return null;
    const top = (side: "A" | "B") => keyness.terms
      .filter((x) => x.favors === side).slice(0, 8);
    return [...top("B")].reverse()
      .map((x) => ({ ...x, value: -x.g2 }))
      .concat(top("A").map((x) => ({ ...x, value: x.g2 })))
      .map((x) => ({ ...x, display: termLabel(x.term, x.category) }));
  }, [keyness]);

  // ---- Panel 3: single-source terms ----
  const fragile = useMemo(() => (sources
    ? sources.filter((x) => x.pages >= 50).slice(0, 8) : null), [sources]);

  if (failed) return null;

  return (
    // Same scaffolding as the chapter's text blocks (TextBlockTwo), so
    // the charts share the prose column's width instead of the page's.
    <div className="evf container">
      <style>{styles}</style>
      {heading && <TextBlock caseTitle={heading} cssClass="mt--100" />}
      <div className="row"><div className="col-lg-12">
        <div className="inner-wrapper"><div className="inner">

      {wants("trends") && shift && (
        <div className="evf-panel">
          <h5>{t("evidence.findShiftTitle",
            "From commemoration toward redress")}</h5>
          <p className="evf-lede">
            {t("evidence.findShiftLede", {
              early: shift.early, late: shift.late,
              defaultValue: "Share of the collection's pages using each term, "
                + "{{early}} against {{late}}.",
            })}
          </p>
          <div className="evf-figure evf-shift">
            <table>
              <thead>
                <tr>
                  <th />
                  <th>{shift.early}</th>
                  <th>{shift.late}</th>
                  <th>{t("evidence.findShiftChange", "Change")}</th>
                </tr>
              </thead>
              {shift.groups.map((group) => (
                <tbody key={group.key} className={group.key}>
                  <tr className="evf-shift-group">
                    <th colSpan={4}>{group.key === "growing"
                      ? t("evidence.findShiftGrowing",
                        "Growing: accountability and repair")
                      : t("evidence.findShiftReceding",
                        "Receding: commemoration and disappearance")}</th>
                  </tr>
                  {group.terms.map((r) => {
                    const early = Math.round(r.early);
                    const late = Math.round(r.late);
                    const delta = late - early;
                    return (
                      <tr key={r.term}>
                        <td className="evf-shift-term">
                          {termLabel(r.term, r.category)}
                        </td>
                        <td>{early}%</td>
                        <td>{late}%</td>
                        <td className="evf-shift-change">
                          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
            <p className="evf-caption">
              {t("evidence.findShiftCaption", {
                earlyPages: shift.earlyPages, latePages: shift.latePages,
                defaultValue: "Change in percentage points · based on "
                  + "{{earlyPages}} and {{latePages}} pages.",
              })}
            </p>
          </div>
        </div>
      )}

      {wants("keyness") && keynessRows && keyness && (
        <div className="evf-panel">
          <h5>{t("evidence.findKeynessTitle",
            "Two collections, two languages of memory")}</h5>
          <p className="evf-lede">
            {t("evidence.findKeynessLede",
              "Dunning log-likelihood (G²): the terms most statistically "
              + "distinctive of each collection.")}
          </p>
          <div className="evf-figure">
            <ResponsiveContainer width="100%"
              height={keynessRows.length * 26 + 40}>
              <BarChart data={keynessRows} layout="vertical"
                margin={{ top: 4, right: 30, left: 30, bottom: 4 }}>
                <XAxis type="number" tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => String(Math.abs(v))} />
                <YAxis type="category" dataKey="display" width={120}
                  tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => Math.abs(v).toFixed(1)} />
                <Bar dataKey="value" barSize={14}>
                  {keynessRows.map((x) => (
                    <Cell key={x.term} fill={categoryColor(x.category)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="evf-caption">
              ← {t("evidence.findDistinctiveOf", "distinctive of")}
              {" "}{keyness.setB} · {keyness.setA}
              {" "}{t("evidence.findDistinctiveOf", "distinctive of")} →
            </p>
          </div>
        </div>
      )}

      {wants("discourses") && communities && communities.length > 0 && (
        <div className="evf-panel">
          <h5>{t("evidence.findDiscoursesTitle", {
            count: communities.filter((c) => c.tier !== "looser").length,
            defaultValue: "{{count}} stable discourses across the categories",
          })}</h5>
          <p className="evf-lede">
            {t("evidence.findDiscoursesLede",
              "Communities detected in the evidence network when "
              + "relationships are weighted by statistical association "
              + "(passage-level PMI), kept only where they recur across a "
              + "few hundred resampled runs; a looser cluster is one whose "
              + "terms group together but only a few hold together reliably. "
              + "Places are not part of the network: the countries listed "
              + "are those whose passages over-represent a discourse, shown "
              + "with their number of passages and flagged when most come "
              + "from one archived site.")}
          </p>
          {communities.map((c) => (
            <div className="evf-community" key={c.name}>
              <div className="evf-community-head">
                <strong>
                  {t(`evidence.community.${c.name}`, c.name)}
                  {c.tier === "looser" && (
                    <span className="evf-tier">
                      {t("evidence.findLooser", "looser cluster")}
                    </span>
                  )}
                </strong>
                <span>{c.size} {t("evidence.findTerms", "terms")}</span>
              </div>
              <div className="evf-comp-bar">
                {c.composition.map((part) => (
                  <span key={part.category} title={`${part.label}: ${part.terms}`}
                    style={{
                      width: `${(part.terms / c.size) * 100}%`,
                      background: categoryColor(part.category),
                    }} />
                ))}
              </div>
              <p className="evf-members">
                {c.members.slice(0, 12).map((m, i) => (
                  <React.Fragment key={m}>
                    {i > 0 && ", "}
                    <span title={glossTitle(m, i18n.language)}>{m}</span>
                  </React.Fragment>
                ))}{c.members.length > 12 ? ", …" : ""}
              </p>
              {c.peripheral && c.peripheral.length > 0 && (
                <p className="evf-peripheral">
                  {t("evidence.findPeripheral", "loosely attached")}:{" "}
                  {c.peripheral.join(", ")}
                </p>
              )}
              {c.places && c.places.length > 0 && (
                <p className="evf-places">
                  <span className="evf-places-label">
                    {t("evidence.findPlaces", "Places")}:
                  </span>{" "}
                  {c.places.map((p, i) => (
                    <React.Fragment key={p.place}>
                      {i > 0 && "; "}
                      {displayTerm(p.place, true)}
                      {" "}({p.passages} {t("evidence.findPassages", "passages")}
                      {p.top_share >= SINGLE_SOURCE_SHARE && (
                        <>, {Math.round(p.top_share * 100)}%{" "}
                          {t("evidence.findOneSite", "from one site")}</>
                      )})
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>
          ))}
          {ties && ties.length > 0 && (
            <div className="evf-ties">
              <h6>{t("evidence.findTiesTitle", "Strongest ties")}</h6>
              <p className="evf-ties-lede">
                {t("evidence.findTiesLede",
                  "The pairs sharing passages far beyond chance (PMI) — "
                  + "precise pairings, not the biggest hubs.")}
              </p>
              {ties.slice(0, 8).map((tie) => (
                <div className="evf-tie" key={`${tie.a}|${tie.b}`}>
                  <span className="pair">
                    <span title={glossTitle(tie.a, i18n.language)}>{tie.a}</span>
                    {" – "}
                    <span title={glossTitle(tie.b, i18n.language)}>{tie.b}</span>
                  </span>
                  <span className="bar" style={{
                    width: `${(tie.pmi / (ties[0]?.pmi || 1)) * 140}px`,
                    background: categoryColor(tie.catA),
                  }} />
                  <span className="pmi">
                    PMI {tie.pmi.toFixed(2)} · {tie.co}{" "}
                    {t("evidence.findTiesShared", "shared passages")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {wants("sources") && fragile && (
        <div className="evf-panel">
          <h5>{t("evidence.findSourcesTitle",
            "Some memories stand on a single source")}</h5>
          <p className="evf-lede">
            {t("evidence.findSourcesLede",
              "Share of a term's pages held by its single largest source "
              + "domain. Terms carried by one archived site are the "
              + "corpus's most fragile memories.")}
          </p>
          <div className="evf-figure" style={{ paddingBottom: 14 }}>
            {fragile.map((x) => (
              <div className="evf-source" key={x.term}>
                <span className="term"
                  title={glossTitle(x.term, i18n.language)}>
                  {termLabel(x.term, x.category)}
                </span>
                <span className="track">
                  <span className="fill" style={{
                    width: `${x.topShare * 100}%`,
                    background: categoryColor(x.category),
                  }} />
                </span>
                <span className="who">
                  {Math.round(x.topShare * 100)}% {x.topDomain}
                  {" · "}{x.effectiveDomains}{" "}
                  {t("evidence.findEffDomains", "eff. domains")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
        </div></div>
      </div></div>
    </div>
  );
};

export default EvidenceFindingsBlock;
